import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "LiquidFiltrationStraining",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Water & Liquid Filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Stainless Steel Mesh for Liquid Filtration & Straining",
      lead: "Engineered stainless steel woven wire mesh, sintered multi-layer media, and custom strainer basket components designed for demanding chemical, water, polymer, and slurry separation with low clean pressure drop and high structural integrity.",
      secondaryCta: {
        label: "Water & Liquid Filtration",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Clean macro photograph of Dutch weave stainless steel wire mesh for liquid filtration",
      },
      cardNote: "All-metal construction & high differential pressure resistance",
      cardLink: {
        label: "Request Technical Review",
        to: "/ContactPage",
      },
      specs: [
        { label: "Filtration Rating", value: "1 µm – 25 mm" },
        { label: "Alloy Selection", value: "AISI 304 / 316L" },
        { label: "Media Construction", value: "Woven / Sintered / Wedge Wire" },
        { label: "Cleanability", value: "Ultrasonic / Backwash" },
      ],
    },
    overview: {
      eyebrow: "Process Engineering",
      heading: "Precision Solid-Liquid Retention Across Continuous Industrial Streams",
      paragraphs: [
        "Industrial liquid processing, chemical clarification, and viscous fluid straining require metallic filter media capable of withstanding continuous hydraulic loads while maintaining accurate particulate cut-offs. Unlike polymeric or disposable elements, all-metal stainless steel media provide dimensional stability, chemical compatibility, and repeat in-line cleanability.",
        "MutualFilter supplies custom-fabricated metallic straining solutions including plain and twilled Dutch weave cloth, heavy-duty square mesh, diffusion-bonded multi-layer sintered laminates, and continuous-slot wedge wire assemblies. Media geometry is tailored to fluid viscosity, suspended particulate geometry, and operational pressure drop thresholds.",
        "Precision-welded from passivated AISI 304 and 316L stainless steel alloys, our strainer baskets, cylindrical elements, and replacement screens resist corrosion from chlorides, industrial process solvents, and aggressive washdown chemistries without fiber shedding or media migration.",
      ],
      keyPoints: [
        "Calibrated Dutch weaves and multilayer sintered composites provide defined surface cut-offs from 1 µm to 25 mm",
        "High open-area geometry preserves low clean differential pressure and maintains sustained flow rates",
        "Rigid all-metal AISI 304 and 316L construction enables ultrasonic bath cleaning, backflushing, and chemical washdown",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Macro detail of precision stainless steel wire cloth for liquid filtration and straining",
      },
    },
    specTable: {
      heading: "Media Configurations for Liquid Filtration & Straining",
      intro: "Standard stainless steel media configurations engineered for inline strainers, pressure vessel housings, and continuous liquid clarification systems.",
      columns: [
        { key: "duty", label: "Process Duty", align: "left" },
        { key: "media", label: "Recommended Media", align: "left" },
        { key: "rating", label: "Aperture / Cut-Off", align: "right" },
        { key: "alloys", label: "Available Alloys", align: "right" },
        { key: "regeneration", label: "Cleaning Method", align: "right" },
      ],
      rows: [
        {
          duty: "Fine Chemical & Water Clarification",
          media: "Twilled Dutch weave / 5-layer sintered mesh",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          regeneration: "Ultrasonic / reverse pulse",
        },
        {
          duty: "Process Liquid & Resin Straining",
          media: "Plain Dutch weave / sintered cylinder",
          rating: "20 µm – 200 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Automated backwash",
        },
        {
          duty: "Pump Protection & Basket Straining",
          media: "Perforated plate lined square mesh",
          rating: "200 µm – 2,000 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Manual wash / spray bar",
        },
        {
          duty: "Coarse Slurry & Viscous Fluid Straining",
          media: "Continuous-slot wedge wire cylinder",
          rating: "0.25 mm – 5.0 mm",
          alloys: "AISI 304 / 316L",
          regeneration: "Mechanical scraper / wash",
        },
      ],
      caption: "All mesh ratings, basket dimensions, flange connections, and perforation backing specs are manufactured to customer drawings and operational requirements.",
    },
    features: {
      heading: "Operational & Manufacturing Advantages",
      intro: "Our liquid filtration and straining media provide mechanical strength, non-shedding purity, and repeatable regenerability across demanding production environments.",
      items: [
        {
          title: "Zero Media Migration",
          text: "Solid metallic wire intersections and diffusion-bonded sinter joints eliminate fiber shedding, preventing downstream contamination in high-purity fluid lines.",
        },
        {
          title: "High Hydraulic Rigidity",
          text: "Engineered core supports, heavy perforated backer tubes, and reinforced basket rings maintain geometric stability against pressure surges and hydraulic hammer.",
        },
        {
          title: "Corrosion & Solvent Resistance",
          text: "Passivated AISI 304 and molybdenum-bearing 316L stainless steel alloys withstand aggressive solvents, aqueous salt solutions, and routine CIP cleaning.",
        },
        {
          title: "Complete Regeneration",
          text: "Smooth metallic surface topology allows swift particulate detachment during automated backflushing, solvent immersion, or ultrasonic bath restoration.",
        },
      ],
    },
    related: {
      heading: "Related Applications & Filtration Media",
      intro: "Explore complementary liquid separation systems and precision metallic filter media manufactured by MutualFilter.",
      cards: [
        {
          title: "Water & Liquid Filtration",
          text: "Engineered metallic filtration solutions for municipal water, process cooling loops, and industrial effluent clarification.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Parent Application",
          linkLabel: "View Application Overview",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Water and liquid filtration overview showing stainless steel separation media",
          },
        },
        {
          title: "Wastewater Filtration",
          text: "Heavy-duty wire mesh and continuous-slot wedge wire screens designed for municipal and industrial wastewater clarification.",
          to: "/WastewaterFiltrationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Wastewater",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Industrial dewatering and rotary drum screening equipment fitted with stainless steel filter mesh",
          },
        },
        {
          title: "Industrial Water Filtration",
          text: "Process water intake, cooling circuits, and pump protection screens engineered for minimal clean pressure drop and high throughput.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Industrial Water",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Industrial stainless steel water filtration equipment and precision screen installation",
          },
        },
        {
          title: "Filter Components",
          text: "Custom fabricated stainless steel filter tubes, cylindrical elements, and strainer baskets for automated backwash separation housings.",
          to: "/FilterComponentsPage",
          badge: "Recommended Product",
          linkLabel: "Explore Filter Components",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Stainless steel welded and sintered filter tubes and cylinder screen components",
          },
        },
        {
          title: "Woven Wire Mesh",
          text: "Precision square weave cloth, plain Dutch weaves, and high-density twilled Dutch weaves for surface filtration and liquid clarification.",
          to: "/WovenWireMeshPage",
          badge: "Recommended Product",
          linkLabel: "Explore Woven Mesh",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Plain Dutch weave stainless steel wire cloth for liquid filtration",
          },
        },
      ],
    },
    cta: {
      id: "liquid-straining-quote",
      eyebrow: "Engineering Consultation",
      heading: "Request a Custom Liquid Straining or Filter Mesh Specification",
      lead: "Provide your required filtration rating, operating fluid type, basket or element dimensions, and alloy grade for a dedicated technical review and quotation.",
      secondaryLabel: "Water & Liquid Filtration",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "LiquidFiltrationStraining",
    relatedTopic: "water",
    hero: {
      categoryBadge: "水与液体过滤",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "液体过滤与拦截用不锈钢丝网与滤芯",
      lead: "专为工业流体澄清、高压浆料拦截、化学药液精滤及循环水系统打造的高精度不锈钢编织丝网、多层烧结介质与滤篮构件，兼具超低初始流动阻力与卓越的机械刚性。",
      secondaryCta: {
        label: "水与液体过滤总览",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "用于液体过滤的不锈钢密纹编织金属丝网微观截留结构",
      },
      cardNote: "全金属刚性结构与耐压差抗冲击性能",
      cardLink: {
        label: "技术方案咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "截留精度范围", value: "1 µm – 25 mm" },
        { label: "常用材质牌号", value: "AISI 304 / 316L" },
        { label: "结构工艺形式", value: "编织 / 烧结 / 楔形丝" },
        { label: "清洗再生方式", value: "超声波 / 反冲洗" },
      ],
    },
    overview: {
      eyebrow: "工艺工程解析",
      heading: "连续工业流体工况下的高精度固液截留技术",
      paragraphs: [
        "工业流体处理、化学溶剂澄清以及高粘度介质拦截系统，要求金属过滤介质能够长周期承受流体水力负荷，同时保证微米级孔径精度的恒定。相比聚合物或一次性耗材滤芯，全金属不锈钢过滤介质具备优异的抗变形能力、化学兼容性与长效在线可再生性。",
        "MutualFilter 提供定制加工的全金属液体筛滤方案，涵盖平纹与斜纹荷兰编织滤布、重载工业方孔网、扩散焊接多层烧结复合板以及连续缝隙楔形丝构件。介质几何构型根据流体粘度、悬浮颗粒形态及允许流动压降指标进行精准匹配。",
        "产品选用经酸洗钝化处理的 AISI 304 与 316L 不锈钢精工焊接制造，有效抵御氯离子、工业有机溶剂以及酸碱在线清洗药剂侵蚀，杜绝纤维脱落及二次介质迁移污染。",
      ],
      keyPoints: [
        "精密荷兰编织与多层烧结复合介质提供 1 µm 至 25 mm 范围内的稳定表面微米截留",
        "高开孔率编织几何结构确保大通量平稳循环，并有效控制初始压降",
        "刚性全金属 AISI 304 与 316L 结构支持超声波清洗、脉冲反冲洗及化学浸泡再生",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "液体过滤与筛滤用高精度不锈钢丝网微观结构",
      },
    },
    specTable: {
      heading: "液体过滤与拦截常用滤材配置参数",
      intro: "以下列出适用于管路过滤器、压力容器滤芯及连续流体澄清系统的典型金属过滤介质配置。",
      columns: [
        { key: "duty", label: "工艺工况", align: "left" },
        { key: "media", label: "推荐过滤介质", align: "left" },
        { key: "rating", label: "截留孔径 / 精度", align: "right" },
        { key: "alloys", label: "可选合金材质", align: "right" },
        { key: "regeneration", label: "推荐再生方式", align: "right" },
      ],
      rows: [
        {
          duty: "精细化工与工艺水深度澄清",
          media: "斜纹荷兰编织网 / 五层烧结复合网",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          regeneration: "超声波 / 反向脉冲",
        },
        {
          duty: "工艺流体净化与树脂颗粒拦截",
          media: "平纹荷兰编织网 / 烧结滤筒",
          rating: "20 µm – 200 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "自动化反冲洗",
        },
        {
          duty: "水泵吸入保护与篮式过滤器",
          media: "冲孔板衬托方孔编织丝网",
          rating: "200 µm – 2,000 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "人工冲洗 / 喷淋管",
        },
        {
          duty: "高固含浆料与粘性流体粗滤",
          media: "连续缝隙楔形丝滤筒",
          rating: "0.25 mm – 5.0 mm",
          alloys: "AISI 304 / 316L",
          regeneration: "机械刮板 / 反冲洗",
        },
      ],
      caption: "各项截留精度、滤篮外形尺寸、法兰接口与冲孔加强骨架均可根据客户图纸及工况要求定制加工。",
    },
    features: {
      heading: "关键运行与结构制造技术优势",
      intro: "我们的液体过滤与拦截介质兼具结构抗压刚性、无脱落洁净度与长周期多次再生能力，适应严苛的工业生产环境。",
      items: [
        {
          title: "零介质脱落污染",
          text: "牢固的金属丝织物节点与扩散焊接烧结层杜绝纤维或碎屑脱落，避免对下游高洁净度流体管路造成二次污染。",
        },
        {
          title: "高水力抗压刚性",
          text: "结合加强型内支撑骨架、厚壁冲孔保护套及坚固滤篮法兰圈，在压力波动及水锤工况下保持孔格几何不变形。",
        },
        {
          title: "优异耐腐蚀与耐溶剂性能",
          text: "酸洗钝化 AISI 304 与含钼 316L 不锈钢有效抵抗强极性溶剂、盐水溶液及常规 CIP 酸碱在线清洗试剂。",
        },
        {
          title: "高效完整的再生性能",
          text: "平滑的金属表面拓扑微观结构有利于沉积物在自动反冲洗、溶剂浸润或超声波清洗中快速剥离脱落。",
        },
      ],
    },
    related: {
      heading: "相关水处理应用与推荐产品",
      intro: "浏览 MutualFilter 为工业流体与水处理领域打造的互补应用方案及精密金属过滤产品。",
      cards: [
        {
          title: "水与液体过滤",
          text: "涵盖工业冷却循环水、原水取水拦截、液体精细筛滤及工业废液分离的全金属介质综合方案。",
          to: "/WaterLiquidFiltrationPage",
          badge: "父级应用分类",
          linkLabel: "查看应用总览",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "水与液体过滤总览及不锈钢过滤材料",
          },
        },
        {
          title: "废水过滤",
          text: "专为市政污水固液分离、原水初筛、悬浮物拦截与污泥脱水系统设计的高性能筛网构件。",
          to: "/WastewaterFiltrationPage",
          badge: "同级应用",
          linkLabel: "探索废水过滤",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "配置不锈钢滤网的工业脱水与滚筒筛分设备",
          },
        },
        {
          title: "工业水过滤",
          text: "工业循环冷却水、工艺给水及水泵吸入口保护滤网，具有超低初始流动压降与大通量特征。",
          to: "/IndustrialWaterFiltrationPage",
          badge: "同级应用",
          linkLabel: "探索工业水过滤",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "工业不锈钢水过滤设备与精密反冲洗滤网组件安装展示",
          },
        },
        {
          title: "过滤组件",
          text: "定制焊接与烧结圆筒滤筒、自清洗过滤器内网与滤篮组件，匹配各类工业自清洗设备。",
          to: "/FilterComponentsPage",
          badge: "推荐产品",
          linkLabel: "探索过滤组件",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "不锈钢焊接与烧结圆筒滤筒滤网构件",
          },
        },
        {
          title: "编织丝网",
          text: "标准方孔不锈钢网、平纹荷兰编织网与高密度斜纹荷兰网，专用于精密液体澄清与固液拦截。",
          to: "/WovenWireMeshPage",
          badge: "推荐产品",
          linkLabel: "探索编织丝网",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "水与液体过滤用平纹荷兰编织不锈钢滤网",
          },
        },
      ],
    },
    cta: {
      id: "liquid-straining-quote",
      eyebrow: "定制方案咨询",
      heading: "获取液体过滤与拦截滤材技术报价",
      lead: "请提供您的目标精度、介质流体特性、滤篮或滤筒尺寸及合金材质要求，我们的工程团队将为您提供针对性的技术选型与报价方案。",
      secondaryLabel: "返回水与液体过滤",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "材质报告 (MTR) 与化学成分检测 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "LiquidFiltrationStraining",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Filtración de Agua y Líquidos",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Mallas de Acero Inoxidable para Filtración y Colado de Líquidos",
      lead: "Mallas de alambre tejidas en acero inoxidable, medios sinterizados multicapa y cestas filtrantes a medida para separación química, de agua, polímeros y suspensiones con baja pérdida de carga y alta integridad mecánica.",
      secondaryCta: {
        label: "Filtración de Agua y Líquidos",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Malla de alambre de acero inoxidable de tejido Dutch para filtración de líquidos",
      },
      cardNote: "Construcción totalmente metálica y alta resistencia a presión diferencial",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Grado de Filtración", value: "1 µm – 25 mm" },
        { label: "Aleaciones", value: "AISI 304 / 316L" },
        { label: "Construcción", value: "Tejido / Sinterizado / Cuña" },
        { label: "Limpieza", value: "Ultrasónica / Retrolavado" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de Procesos",
      heading: "Retención Sólido-Líquido de Precisión en Circuitos Industriales Continuos",
      paragraphs: [
        "El tratamiento de fluidos industriales, la clarificación química y el colado de suspensiones viscosas requieren medios filtrantes metálicos capaces de soportar cargas hidráulicas continuas manteniendo una retención micrométrica exacta. A diferencia de los elementos poliméricos o desechables, el acero inoxidable ofrece estabilidad dimensional, compatibilidad química y limpieza en línea repetible.",
        "MutualFilter suministra soluciones metálicas a medida que incluyen telas de tejido Dutch liso y cruzado, mallas cuadradas pesadas, laminados sinterizados multicapa por difusión y conjuntos de alambre en cuña de ranura continua. La geometría se selecciona en función de la viscosidad del fluido, el tipo de partículas y la pérdida de carga admisible.",
        "Soldados con precisión en aceros inoxidables pasivados AISI 304 y 316L, nuestras cestas coladoras, cartuchos cilíndricos y mallas de recambio resisten el ataque de cloruros, disolventes y productos químicos de limpieza CIP sin desprendimiento de fibras ni migración del medio.",
      ],
      keyPoints: [
        "Tejidos Dutch calibrados y compuestos sinterizados proporcionan cortes micrométricos superficiales definidos de 1 µm a 25 mm",
        "Geometrías con gran área abierta que minimizan la pérdida de carga diferencial y sostienen caudales continuos",
        "Estructura metálica rígida en AISI 304 y 316L apta para baños de ultrasonidos, retrolavados y lavados químicos",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Detalle macroscópico de tela metálica de acero inoxidable para filtración de líquidos",
      },
    },
    specTable: {
      heading: "Configuraciones de Medios para Filtración y Colado de Líquidos",
      intro: "Medios filtrantes metálicos estándar para filtros de tubería, carcasas a presión y sistemas de clarificación continua.",
      columns: [
        { key: "duty", label: "Aplicación de Proceso", align: "left" },
        { key: "media", label: "Medio Recomendado", align: "left" },
        { key: "rating", label: "Corte / Abertura Nominal", align: "right" },
        { key: "alloys", label: "Aleaciones Disponibles", align: "right" },
        { key: "regeneration", label: "Método de Limpieza", align: "right" },
      ],
      rows: [
        {
          duty: "Clarificación Química Fina y Agua",
          media: "Tejido Dutch cruzado / sinterizado 5 capas",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          regeneration: "Ultrasonidos / pulso inverso",
        },
        {
          duty: "Filtración de Fluidos y Retención de Resinas",
          media: "Tejido Dutch liso / cilindro sinterizado",
          rating: "20 µm – 200 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Retrolavado automático",
        },
        {
          duty: "Protección de Bombas y Cestas Coladoras",
          media: "Malla cuadrada con chapa perforada",
          rating: "200 µm – 2.000 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Lavado manual / aspersión",
        },
        {
          duty: "Suspensiones Gruesas y Fluidos Viscosos",
          media: "Cilindro de alambre en cuña",
          rating: "0,25 mm – 5,0 mm",
          alloys: "AISI 304 / 316L",
          regeneration: "Rascador mecánico / lavado",
        },
      ],
      caption: "Todas las dimensiones de cestas, conexiones por brida y refuerzos perforados se fabrican según especificaciones del cliente.",
    },
    features: {
      heading: "Ventajas Operativas y de Fabricación",
      intro: "Nuestros medios filtrantes para líquidos combinan solidez mecánica, pureza sin desprendimiento y completa regenerabilidad.",
      items: [
        {
          title: "Cero Migración de Fibras",
          text: "Las uniones metálicas soldadas y los sinterizados por difusión evitan el desprendimiento de fibras, preservando la pureza de las líneas de fluidos.",
        },
        {
          title: "Alta Rigidez Hidráulica",
          text: "Núcleos de soporte robustos y aros reforzados garantizan la estabilidad geométrica ante pulsos de presión y golpes de ariete.",
        },
        {
          title: "Resistencia a la Corrosión",
          text: "Aleaciones pasivadas AISI 304 y 316L con molibdeno que soportan disolventes, soluciones salinas y productos cáusticos CIP.",
        },
        {
          title: "Regeneración Completa",
          text: "Superficies metálicas lisas que permiten un desprendimiento rápido de sólidos durante el retrolavado o baños por ultrasonidos.",
        },
      ],
    },
    related: {
      heading: "Aplicaciones y Medios Relacionados",
      intro: "Explore soluciones complementarias en filtración de líquidos y medios metálicos de precisión diseñados por MutualFilter.",
      cards: [
        {
          title: "Filtración de Agua y Líquidos",
          text: "Soluciones de separación metálica para agua municipal, refrigeración de procesos y tratamiento de efluentes.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Aplicación Principal",
          linkLabel: "Ver Visión General",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Visión general de filtración de agua y medios metálicos",
          },
        },
        {
          title: "Filtración de Aguas Residuales",
          text: "Mallas y cribas de alambre en cuña diseñadas para desbaste primario y deshidratación de efluentes.",
          to: "/WastewaterFiltrationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Aguas Residuales",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Equipo industrial de deshidratación con malla filtrante de acero inoxidable",
          },
        },
        {
          title: "Filtración de Agua Industrial",
          text: "Mallas para tomas de agua, torres de refrigeración y protección de bombas con mínima pérdida de carga inicial.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Agua Industrial",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Instalación de filtración de agua industrial con mallas de acero inoxidable",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Tubos soldados, cilindros sinterizados y cestas filtrantes fabricados a medida para carcasas de retrolavado.",
          to: "/FilterComponentsPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubos y cilindros filtrantes de acero inoxidable soldados y sinterizados",
          },
        },
        {
          title: "Malla de Alambre Tejida",
          text: "Mallas cuadradas de precisión, tejidos Dutch lisos y tejidos Dutch cruzados para clarificación de fluidos.",
          to: "/WovenWireMeshPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Malla Tejida",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Malla de alambre de tejido Dutch liso para filtración de líquidos",
          },
        },
      ],
    },
    cta: {
      id: "liquid-straining-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite Cotización para Mallas y Elementos de Colado de Líquidos",
      lead: "Indique el grado de filtración requerido, tipo de fluido, dimensiones de la cesta o cartucho y aleación de acero para una revisión técnica y cotización.",
      secondaryLabel: "Volver a Filtración de Agua",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Informes de ensayo de material (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "LiquidFiltrationStraining",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Wasser- & Flüssigkeitsfiltration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Edelstahldrahtgewebe für die Flüssigkeitsfiltration & Siebung",
      lead: "Gefertigte Edelstahl-Drahtgewebe, mehrlagige Sintermedien und Siebkörbe für anspruchsvolle Trennaufgaben in Chemie-, Wasser-, Polymer- und Schlammanwendungen mit minimalem Druckverlust und hoher Druckdifferenzfestigkeit.",
      secondaryCta: {
        label: "Wasser- & Flüssigkeitsfiltration",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Edelstahl-Tressengewebe für die Flüssigkeitsfiltration",
      },
      cardNote: "Ganzmetallkonstruktion & hohe Differenzdruckfestigkeit",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Filterfeinheit", value: "1 µm – 25 mm" },
        { label: "Werkstoffe", value: "AISI 304 / 316L" },
        { label: "Medienaufbau", value: "Gewebe / Sintermetall / Spaltsieb" },
        { label: "Reinigbarkeit", value: "Ultraschall / Rückspülung" },
      ],
    },
    overview: {
      eyebrow: "Verfahrenstechnik",
      heading: "Präzise Fest-Flüssig-Abscheidung in kontinuierlichen Industrieprozessen",
      paragraphs: [
        "Industrielle Prozessflüssigkeiten, chemische Klärung und das Sieben hochviskoser Medien erfordern metallische Filtermedien, die dauerhaften hydraulischen Belastungen standhalten und präzise Trenngrenzen sichern. Im Vergleich zu Einweg- oder Polymerelementen bieten Edelstahlmedien Formstabilität, Chemikalienbeständigkeit und wiederholte Regenerierbarkeit.",
        "MutualFilter liefert kundenspezifische Metallfilterlösungen wie glatte und Köpertressengewebe, schwere Quadratmaschengewebe, diffusionsgebundene Mehrlagen-Sinterplatten und kontinuierliche Spaltsiebbaugruppen. Die Porengeometrie wird exakt auf Viskosität, Feststofffracht und maximal zulässigen Druckverlust abgestimmt.",
        "Gefertigt aus passiviertem Edelstahl AISI 304 und 316L widerstehen unsere Filterkörbe, Filterzylinder und Siebeinsätze aggressiven Lösemitteln, Chloriden und alkalischen CIP-Spülungen ohne Faserabgabe oder Medienmigration.",
      ],
      keyPoints: [
        "Präzise Tressengewebe und Sinterverbunde sichern definierte Oberflächentrenngrenzen von 1 µm bis 25 mm",
        "Hohe freie Siebflächen minimieren den hydraulischen Differenzdruck und gewährleisten stabilen Durchfluss",
        "Formstabile Ganzmetallkonstruktion aus AISI 304 und 316L ermöglicht Ultraschallreinigung, Rückspülung und chemische Spülung",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Makroaufnahme von Edelstahldrahtgewebe für die Flüssigkeitsfiltration",
      },
    },
    specTable: {
      heading: "Filtermedien-Konfigurationen für Flüssigkeitstrennung & Siebung",
      intro: "Standard-Ganzmetallkonfigurationen für Rohrleitungsfilter, Druckbehälter und kontinuierliche Flüssigkeitsklärsysteme.",
      columns: [
        { key: "duty", label: "Prozessaufgabe", align: "left" },
        { key: "media", label: "Empfohlenes Filtermedium", align: "left" },
        { key: "rating", label: "Trenngrenze / Maschenweite", align: "right" },
        { key: "alloys", label: "Verfügbare Werkstoffe", align: "right" },
        { key: "regeneration", label: "Reinigungsmethode", align: "right" },
      ],
      rows: [
        {
          duty: "Chemische Feinklärung & Reinstwasser",
          media: "Köpertresse / 5-Lagen-Sintergewebe",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          regeneration: "Ultraschall / Gegenpuls",
        },
        {
          duty: "Prozessflüssigkeiten & Harzrückhalt",
          media: "Glatte Tresse / Sinterfilterrohr",
          rating: "20 µm – 200 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Automatische Rückspülung",
        },
        {
          duty: "Pumpenschutz & Siebkörbe",
          media: "Lochblechgestütztes Quadratgewebe",
          rating: "200 µm – 2.000 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Manuelle Spülung / Sprührohr",
        },
        {
          duty: "Grobschlämme & viskose Flüssigkeiten",
          media: "Spaltsiebrohr mit kontinuierlichem Schlitz",
          rating: "0,25 mm – 5,0 mm",
          alloys: "AISI 304 / 316L",
          regeneration: "Mechanischer Schaber / Spülung",
        },
      ],
      caption: "Alle Abmessungen, Filterkorbgeometrien, Flanschanschlüsse und Stützlochbleche werden auftragsspezifisch nach Zeichnung gefertigt.",
    },
    features: {
      heading: "Betriebliche & Fertigungstechnische Vorteile",
      intro: "Unsere Medien für die Flüssigkeitsfiltration verbinden mechanische Robustheit, faserfreie Reinheit und langlebige Regenerierbarkeit.",
      items: [
        {
          title: "Keine Medienmigration",
          text: "Feste Drahtbindungen und diffusionsgebundene Sinterstellen verhindern Faserabrieb und sichern reine Fluidströme ohne Verunreinigungen.",
        },
        {
          title: "Hohe Hydraulische Stabilität",
          text: "Stützkörper, Lochblechmanschetten und verstärkte Flanschringe widerstehen Druckspitzen und hydraulischen Druckschlägen ohne Verformung.",
        },
        {
          title: "Korrosions- & Lösemittelfestigkeit",
          text: "Passivierte Güten AISI 304 und molybdänhaltiges 316L widerstehen organischen Lösungsmitteln, Salzlösungen und alkalischen CIP-Reinigern.",
        },
        {
          title: "Vollständige Regenerierbarkeit",
          text: "Glatte Metalloberflächen erleichtern das Lösen von Feststoffen bei automatischer Rückspülung, Lösemittelbädern oder Ultraschall.",
        },
      ],
    },
    related: {
      heading: "Verwandte Anwendungen & Filtermedien",
      intro: "Entdecken Sie ergänzende Flüssigkeitsabscheidungssysteme und metallische Präzisionsfiltermedien von MutualFilter.",
      cards: [
        {
          title: "Wasser- & Flüssigkeitsfiltration",
          text: "Metallische Gesamtlösungen für Prozesswasser, Kühlkreisläufe und industrielle Abwasseraufbereitung.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Hauptanwendung",
          linkLabel: "Übersicht ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Wasser- und Flüssigkeitsfiltration Übersicht mit Edelstahl-Filtermedien",
          },
        },
        {
          title: "Abwasserfiltration",
          text: "Schwere Drahtgewebe und Spaltsiebe für kommunale und industrielle Abwasserklärung und Schlammentwässerung.",
          to: "/WastewaterFiltrationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Abwasser ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Industrielle Entwässerungs- und Trommelsiebanlage mit Edelstahl-Filtergewebe",
          },
        },
        {
          title: "Industrielle Wasserfiltration",
          text: "Sieblösungen für Kühlkreisläufe, Entnahmesiebe und Pumpenschutz mit minimalem Anfangsdruckverlust.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Industriewasser ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Industrielle Edelstahl-Wasserfiltrationsausrüstung",
          },
        },
        {
          title: "Filterkomponenten",
          text: "Geschweißte und gesinterte Filterrohre, Zylinder und Siebkörbe für automatische Rückspülfiltergehäuse.",
          to: "/FilterComponentsPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Filterkomponenten entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Geschweißte und gesinterte Filterrohre aus Edelstahl",
          },
        },
        {
          title: "Drahtgewebe",
          text: "Präzisions-Quadratmaschen, glatte Tressen und hochdichte Köpertressen für Oberflächenfiltration und Flüssigklärung.",
          to: "/WovenWireMeshPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Drahtgewebe entdecken",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Glatte Tresse Edelstahldrahtgewebe für die Flüssigfiltration",
          },
        },
      ],
    },
    cta: {
      id: "liquid-straining-quote",
      eyebrow: "Technische Anfrage",
      heading: "Angebot für Flüssigkeitsfiltermedien & Siebeinsätze anfordern",
      lead: "Nennen Sie Filterfeinheit, Fluidart, Abmessungen des Korbs oder Filterelements sowie den gewünschten Werkstoff für eine technische Prüfung.",
      secondaryLabel: "Zurück zu Wasserfiltration",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "LiquidFiltrationStraining",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Filtration d'Eau et de Liquides",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toiles en Acier Inoxydable pour Filtration et Tamisage de Liquides",
      lead: "Toiles métalliques tissées en acier inoxydable, médias frittés multicouches et paniers filtrants sur mesure pour la séparation d'eaux, polymères, boues et fluides chimiques avec faible perte de charge et haute résistance mécanique.",
      secondaryCta: {
        label: "Filtration d'Eau et de Liquides",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Toile métallique en acier inoxydable à tissage hollandais pour filtration de liquides",
      },
      cardNote: "Conception tout métal et haute résistance à la pression différentielle",
      cardLink: {
        label: "Consultation Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Seuil de Filtration", value: "1 µm – 25 mm" },
        { label: "Nuances d'Alliages", value: "AISI 304 / 316L" },
        { label: "Structure du Média", value: "Tissé / Fritté / Fil Profilé" },
        { label: "Nettoyabilité", value: "Ultrasons / Rétrolavage" },
      ],
    },
    overview: {
      eyebrow: "Génie des Procédés",
      heading: "Rétention Solide-Liquide de Précision dans les Procédés Industriels Continus",
      paragraphs: [
        "Le traitement des liquides industriels, la clarification chimique et le tamisage de fluides visqueux requièrent des médias métalliques capables d'endurer des charges hydrauliques continues tout en maintenant un seuil de coupure précis. À la différence des cartouches jetables ou polymères, l'acier inoxydable assure stabilité dimensionnelle, inertie chimique et régénération répétée.",
        "MutualFilter conçoit des solutions métalliques sur mesure comprenant des toiles hollandaises unies et sergées, des toiles à mailles carrées lourdes, des stratifiés frittés multicouches et des ensembles à fil profilé à fente continue. La configuration est adaptée à la viscosité, aux particules en suspension et à la perte de charge admise.",
        "Fabriqués en acier inoxydable passivé AISI 304 et 316L, nos paniers filtrants, cartouches cylindriques et toiles de rechange résistent aux chlorures, solvants et cycles de nettoyage NEP sans relargage de fibres ni migration du média.",
      ],
      keyPoints: [
        "Toiles hollandaises calibrées et complexes frittés assurant des coupures nettes en surface de 1 µm à 25 mm",
        "Grandes surfaces ouvertes réduisant la perte de charge différentielle et maintenant un débit continu",
        "Conception rigide en inox AISI 304 et 316L permettant le nettoyage par ultrasons, contre-lavage et bains chimiques",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Détail macro d'une toile métallique en acier inoxydable pour filtration de liquides",
      },
    },
    specTable: {
      heading: "Configurations de Médias pour Filtration et Tamisage de Liquides",
      intro: "Médias métalliques types pour filtres de tuyauterie, corps sous pression et systèmes de clarification continue.",
      columns: [
        { key: "duty", label: "Fonction de Procédé", align: "left" },
        { key: "media", label: "Média Filtrant Recommandé", align: "left" },
        { key: "rating", label: "Seuil / Ouverture Nominale", align: "right" },
        { key: "alloys", label: "Nuances Disponibles", align: "right" },
        { key: "regeneration", label: "Mode de Régénération", align: "right" },
      ],
      rows: [
        {
          duty: "Clarification Chimique Fine & Eau de Procédé",
          media: "Tissage hollandais sergé / fritté 5 couches",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          regeneration: "Ultrasons / contre-pulsion",
        },
        {
          duty: "Filtration de Fluides & Rétention de Résines",
          media: "Tissage hollandais uni / cylindre fritté",
          rating: "20 µm – 200 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Rétrolavage automatique",
        },
        {
          duty: "Protection des Pompes & Paniers Filtrants",
          media: "Toile maille carrée sur tôle perforée",
          rating: "200 µm – 2 000 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Lavage manuel / rampe de buses",
        },
        {
          duty: "Boues Épaisses & Fluides Visqueux",
          media: "Tube cylindrique à fil profilé en V",
          rating: "0,25 mm – 5,0 mm",
          alloys: "AISI 304 / 316L",
          regeneration: "Racleur mécanique / lavage",
        },
      ],
      caption: "Dimensions des paniers, brides de raccordement et tôles perforées de soutien fabriquées selon plans clients.",
    },
    features: {
      heading: "Atouts Fonctionnels et de Fabrication",
      intro: "Nos toiles et filtres pour liquides allient résistance mécanique, propreté sans relargage et nettoyabilité pérenne.",
      items: [
        {
          title: "Zéro Migration de Média",
          text: "Les soudures métalliques et les liaisons frittées par diffusion évitent tout relargage particulaire dans les circuits de fluides critiques.",
        },
        {
          title: "Haute Rigidité Hydraulique",
          text: "Tubes perforés de soutien et bagues renforcées assurant la tenue géométrique face aux transitoires et coups de bélier.",
        },
        {
          title: "Résistance aux Solvants et Corrosions",
          text: "Aciers inoxydables passivés AISI 304 et 316L au molybdène résistant aux solvants, saumures et nettoyants alcalins NEP.",
        },
        {
          title: "Régénération Complète",
          text: "Surfaces métalliques lisses favorisant le décollement rapide des matières colmatantes lors des rétrolavages ou ultrasons.",
        },
      ],
    },
    related: {
      heading: "Applications et Produits Associés",
      intro: "Découvrez les solutions complémentaires de séparation liquide et les médias métalliques fabriqués par MutualFilter.",
      cards: [
        {
          title: "Filtration d'Eau et de Liquides",
          text: "Solutions de séparation métallique pour eaux industrielles, circuits de refroidissement et traitement des effluents.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Application Principale",
          linkLabel: "Voir la Présentation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Présentation générale de la filtration d'eau avec toiles métalliques",
          },
        },
        {
          title: "Filtration des Eaux Usées",
          text: "Toiles tissées lourdes et grilles à fil profilé pour le dégrillage d'effluents et l'essorage de boues.",
          to: "/WastewaterFiltrationPage",
          badge: "Application Associée",
          linkLabel: "Explorer les Eaux Usées",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Équipement industriel d'eaux usées avec tamis en acier inoxydable",
          },
        },
        {
          title: "Filtration d'Eau Industrielle",
          text: "Tamis pour prises d'eau, circuits de refroidissement et protection des pompes avec faible perte de charge.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Application Associée",
          linkLabel: "Explorer l'Eau Industrielle",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Installation de filtration d'eau industrielle en acier inoxydable",
          },
        },
        {
          title: "Composants Filtrants",
          text: "Tubes soudés, cylindres frittés et paniers métalliques adaptés aux carters de filtres autonettoyants.",
          to: "/FilterComponentsPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Composants",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubes et cylindres filtrants en acier inoxydable soudés et frittés",
          },
        },
        {
          title: "Toile Métallique Tissée",
          text: "Mailles carrées de précision, toiles hollandaises unies et sergées pour la clarification de liquides.",
          to: "/WovenWireMeshPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer la Toile Tissée",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Toile métallique inox tissage hollandais uni pour filtration liquide",
          },
        },
      ],
    },
    cta: {
      id: "liquid-straining-quote",
      eyebrow: "Étude Technique",
      heading: "Demandez un Devis pour Éléments et Toiles de Tamisage Liquide",
      lead: "Indiquez votre seuil de filtration, la nature du fluide, les dimensions de panier ou cartouche et la nuance d'acier pour une étude sur mesure.",
      secondaryLabel: "Retour à la Filtration d'Eau",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Certificats matière (MTR) et contrôles PMI disponibles sur demande selon spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "LiquidFiltrationStraining",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Filtração de Água e Líquidos",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malha de Aço Inoxidável para Filtração e Retenção de Líquidos",
      lead: "Malhas tecidas em aço inoxidável, meios sinterizados multicamadas e cestos filtrantes sob medida para separação de água, polímeros, polpas e produtos químicos com baixa queda de pressão e alta integridade estrutural.",
      secondaryCta: {
        label: "Filtração de Água e Líquidos",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Malha de arame de aço inoxidável em trama holandesa para filtração de líquidos",
      },
      cardNote: "Construção metálica e resistência à pressão diferencial",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Grau de Filtração", value: "1 µm – 25 mm" },
        { label: "Ligas de Aço", value: "AISI 304 / 316L" },
        { label: "Construção", value: "Tecido / Sinterizado / Cunha" },
        { label: "Limpabilidade", value: "Ultrassônica / Retrolavagem" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de Processos",
      heading: "Retenção Sólido-Líquido de Precisão em Fluxos Industriais Contínuos",
      paragraphs: [
        "Processos com fluidos industriais, clarificação química e retenção em suspensões viscosas exigem meios filtrantes metálicos capazes de suportar cargas hidráulicas contínuas com retenção micrométrica uniforme. Ao contrário de elementos descartáveis de polímero, o aço inoxidável oferece estabilidade dimensional, compatibilidade química e regeneração repetida em linha.",
        "A MutualFilter fabrica soluções metálicas sob medida incluindo telas de trama holandesa lisa e sarja, malhas quadradas pesadas, compósitos sinterizados multicamadas e tubos de tela de arame cunha de ranhura contínua. A geometria é calculada de acordo com a viscosidade do fluido, formato dos sólidos e perda de carga operacional.",
        "Soldados com precisão em aço inoxidável passivado AISI 304 e 316L, nossos cestos filtrantes, cartuchos cilíndricos e telas de reposição resistem à corrosão por cloretos, solventes industriais e limpezas CIP alcalinas sem liberação de fibras ou migração do meio.",
      ],
      keyPoints: [
        "Tramas holandesas calibradas e compósitos sinterizados asseguram pontos de corte definidos de 1 µm a 25 mm",
        "Geometria com ampla área aberta mantém baixa perda de carga diferencial e fluxo sustentado",
        "Construção metálica rígida em AISI 304 e 316L possibilita banhos de ultrassom, retrolavagens e lavagens químicas",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
        alt: "Detalhe em macro de tela metálica de aço inoxidável para filtração de líquidos",
      },
    },
    specTable: {
      heading: "Configurações de Meios para Filtração e Retenção de Líquidos",
      intro: "Meios filtrantes metálicos padrão para filtros tipo cesto, vasos de pressão e clarificação contínua.",
      columns: [
        { key: "duty", label: "Etapa de Processo", align: "left" },
        { key: "media", label: "Meio Filtrante Recomendado", align: "left" },
        { key: "rating", label: "Abertura / Corte Nominal", align: "right" },
        { key: "alloys", label: "Ligas Disponíveis", align: "right" },
        { key: "regeneration", label: "Método de Limpeza", align: "right" },
      ],
      rows: [
        {
          duty: "Clarificação Química Fina e Água",
          media: "Trama holandesa sarja / sinterizado 5 camadas",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L",
          regeneration: "Ultrassom / pulso reverso",
        },
        {
          duty: "Filtração de Fluidos e Retenção de Resinas",
          media: "Trama holandesa lisa / cilindro sinterizado",
          rating: "20 µm – 200 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Retrolavagem automática",
        },
        {
          duty: "Proteção de Bombas e Cestos Filtrantes",
          media: "Malha quadrada apoiada em chapa perfurada",
          rating: "200 µm – 2.000 µm",
          alloys: "AISI 304 / 316L",
          regeneration: "Lavagem manual / aspersão",
        },
        {
          duty: "Polpas Grossas e Fluidos Viscosos",
          media: "Tubo cilíndrico de arame cunha contínuo",
          rating: "0,25 mm – 5,0 mm",
          alloys: "AISI 304 / 316L",
          regeneration: "Raspador mecânico / lavagem",
        },
      ],
      caption: "Todas as dimensões dos cestos, conexões flangeadas e chapas de reforço perfuradas são fabricadas sob desenho do cliente.",
    },
    features: {
      heading: "Destaques Construtivos e Operacionais",
      intro: "Nossos meios de filtração e retenção de líquidos combinam solidez mecânica, pureza sem desprendimento e capacidade de reutilização contínua.",
      items: [
        {
          title: "Zero Migração de Fibras",
          text: "As interseções sólidas de arame e as uniões sinterizadas por difusão impedem a liberação de filamentos ou fragmentos na linha.",
        },
        {
          title: "Alta Rigidez Hidráulica",
          text: "Núcleos internos reforçados e anéis de fixação mantêm a estabilidade geométrica perante picos de pressão e golpes de aríete.",
        },
        {
          title: "Resistência a Solventes e Corrosão",
          text: "Aços inoxidáveis passivados AISI 304 e 316L com molibdênio suportam solventes, salmouras e detergentes cáusticos CIP.",
        },
        {
          title: "Regeneração Completa",
          text: "Superfícies metálicas uniformes proporcionam rápida remoção de sólidos em retrolavagens ou banhos ultrassônicos.",
        },
      ],
    },
    related: {
      heading: "Aplicações e Meios Relacionados",
      intro: "Conheça soluções integradas de separação de líquidos e produtos de filtragem de precisão fabricados pela MutualFilter.",
      cards: [
        {
          title: "Filtração de Água e Líquidos",
          text: "Soluções completas de separação metálica para captação, refrigeração e tratamento de efluentes industriais.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Aplicação Principal",
          linkLabel: "Ver Visão Geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Visão geral de filtração de água e meios metálicos de separação",
          },
        },
        {
          title: "Filtração de Efluentes",
          text: "Telas de alta resistência e peneiras de arame cunha para desaguamento de lodo e gradeamento primário.",
          to: "/WastewaterFiltrationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Efluentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Equipamento industrial de tratamento de efluentes com telas de aço inoxidável",
          },
        },
        {
          title: "Filtração de Água Industrial",
          text: "Telas para circuitos de água de resfriamento, tomadas de processo e proteção de bombas com baixa perda de carga.",
          to: "/IndustrialWaterFiltrationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Água Industrial",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
            alt: "Equipamento industrial de filtração de água em aço inoxidável e tela de precisão",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Tubos soldados, cilindros sinterizados e cestos customizados para carcaças com retrolavagem automática.",
          to: "/FilterComponentsPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubos e cilindros filtrantes soldados e sinterizados em aço inoxidável",
          },
        },
        {
          title: "Telas de Arame Tecidas",
          text: "Malhas quadradas de precisão, tramas holandesas lisas e sarjas densas para clarificação de líquidos.",
          to: "/WovenWireMeshPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Tela Tecida",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Tela de arame inox de trama holandesa lisa para filtração de líquidos",
          },
        },
      ],
    },
    cta: {
      id: "liquid-straining-quote",
      eyebrow: "Consulta de Engenharia",
      heading: "Solicite uma Cotação para Telas e Cestos de Retenção de Líquidos",
      lead: "Informe o grau de filtração desejado, características do fluido, dimensões do cesto ou elemento e a liga requerida para uma análise detalhada.",
      secondaryLabel: "Voltar para Filtração de Água",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
